import React, { Fragment, Suspense, useState } from 'react'
import {
  Link as RouterLink,
  useNavigate,
} from 'react-router-dom'
import { toast } from 'react-toastify'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import CellWifi from '@mui/icons-material/CellWifi'
import CirclesIcon from '@mui/icons-material/Circle'
import Delete from '@mui/icons-material/Delete'
import Description from '@mui/icons-material/Description'
import DialpadOutlinedIcon from '@mui/icons-material/DialpadOutlined'
import FiveGIcon from '@mui/icons-material/FiveG'
import FourGMobiledataIcon from '@mui/icons-material/FourGMobiledata'
/* Comment out for now as we don't show payments
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
*/
import PermPhoneMsgOutlinedIcon from '@mui/icons-material/PermPhoneMsgOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt'
import SimCard from '@mui/icons-material/SimCard'
import SyncSharpIcon from '@mui/icons-material/SyncSharp'
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import {
  GridColDef,
  GridRowsProp,
  GridToolbarContainer
} from '@mui/x-data-grid'
import SimAPI from 'api/orbitV1/SimAPI'
import CustomSearchbar from 'common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import LayoutSkeleton from 'common/Skeletons/LayoutSkeleton'
import BorderLinearProgress from 'common/ui/BorderLinearProgress'
import { EditIcon } from 'common/ui/icons'
import { HtmlTooltip } from 'common/ui/tooltip'
import { getIccid, getMsisdn } from 'helpers/simStatus'
import { formatToPascalCase } from 'helpers/strings'
import { convertBytesToGB, convertMinsToHrs } from 'helpers/usageConverters'
import OrbitSession from 'hooks/useCookies'
import useOrbitFlag from 'hooks/useOrbitFlag'
import { DeleteDialog } from 'pages/Customers/components'
// can be used to count suspended and terminated sims if required
// exported for use in Dashboard
export function countSims(simGroup, teamMembers) {
  let activeSims = 0
  let totalSims = 0
  for (const teamMember of teamMembers) {
    if (teamMember.status === 'Active') {
      activeSims++
    }
    totalSims++
  }
  if (simGroup === 'activeSims') {
    return activeSims
  }

  if (simGroup === 'totalSims') {
    return totalSims
  }
}

export interface TeamMemberProps {
  _id: string
  owner: string
  simOwner: string
  simRemarks: string
  connect: string
  cachedData: string
  remainingData: string
  data: string
  cachedVoice: number
  remainingVoice: number
  voice: number
  cachedSms: number
  remainingSms: number
  sms: string
  status: string
  iccid: string
  msisdn: string
}

interface ISimTable {
  teamMembers: TeamMemberProps[]
  pageRows: (number | { value: number; label: string })[]
  pageNo?: number
  customToolbarComponent: React.ReactNode
  refresh: () => void
}

export function SimTable(props: ISimTable) {
  const { teamMembers, customToolbarComponent, refresh } = props
  const [msisdn, setMsisdn] = useState('')

  /* Dialog Box for deleting sims */
  const [isDialogOpen, setDialogOpen] = useState(false)

  const rows: GridRowsProp = teamMembers.map((teamMember) => {
    return {
      id: teamMember._id,
      name: teamMember.simOwner ?? teamMember.owner,
      msisdn: teamMember.msisdn,
      iccid: teamMember.iccid,
      remarks: teamMember.simRemarks,
      connect: teamMember.connect,
      data: {
        cached: teamMember.cachedData,
        remaining: teamMember.remainingData,
        current: teamMember.data
      },
      voice: {
        cached: teamMember.cachedVoice,
        remaining: teamMember.remainingVoice,
        current: teamMember.voice
      },
      sms: {
        cached: teamMember.cachedSms,
        remaining: teamMember.remainingSms,
        current: teamMember.sms
      },
      status: teamMember.status,
      teamMember
    }
  })

  const handleDelete = (msisdn) => {
    setMsisdn(msisdn)
    setDialogOpen(true)
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>User</strong>
                </Typography>
                <Typography>
                  The user / identity assigned to this account / phone number.
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <PersonOutlineOutlinedIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      flex: 2,
      renderCell: (params) => {
        return formatToPascalCase(params.row.name)
      }
    },
    {
      field: 'msisdn',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>Phone Number</strong>
                </Typography>
                <Typography>The MSISDN of this user.</Typography>
              </Fragment>
            }
          >
            <IconButton>
              <DialpadOutlinedIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: (params) => {
        return getMsisdn(params.row.msisdn)
      }
    },
    {
      field: 'iccid',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>ICCID</strong>
                </Typography>
                <Typography>The ICCID of this user.</Typography>
              </Fragment>
            }
          >
            <IconButton>
              <SimCard />
            </IconButton>
          </HtmlTooltip>
        )
      },
      flex: 2,
      renderCell: (params) => {
        return getIccid(params.row.iccid)
      }
    },
    {
      field: 'data',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>Data Left (GB)</strong>
                </Typography>
                <Typography>
                  The remaining data in GB of this current billing cycle.
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <SignalCellularAltIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: (params) => {
        return (
          <BorderLinearProgress
            current={convertBytesToGB(params.row.data.remaining)}
            total={convertBytesToGB(
              params.row.data.cached || params.row.data.current
            )}
          />
        )
      },
      valueGetter: (params) => {
        return params.row.data.remaining / (params.row.data.cached || params.row.data.current)
      }
    },
    {
      field: 'type',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>Connection Type</strong>
                </Typography>
                <Typography>
                  Type of connection.
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <CellWifi />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: (params) => {
        if (params.row.connect == '5G')
          return (<FiveGIcon />)
        else
          return (<FourGMobiledataIcon />)
      },
      valueGetter: (params) => {
        return params.row.connect
      }
    },
    {
      field: 'voice',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>Voice Left (mins)</strong>
                </Typography>
                <Typography>
                  The remaining voice left in mins of this current billing
                  cycle.
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <PermPhoneMsgOutlinedIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: (params) => {
        if ((params.row.voice.current || params.row.voice.cached) > 1e6) {
          return <AllInclusiveIcon />
        } else if (
          (params.row.voice.cached || params.row.voice.current) <= 1e6 &&
          (params.row.voice.cached || params.row.voice.current) !== 0
        ) {
          return (
            <>
              {convertMinsToHrs(params.row.voice.remaining)}
              &nbsp;/&nbsp;
              <strong>
                {Math.round(
                  convertMinsToHrs(
                    params.row.voice.cached || params.row.voice.current
                  )
                )}
              </strong>
            </>
          )
        } else {
          return <strong style={{ marginBottom: '12px' }}>-</strong>
        }
      },
      valueGetter: (params) => {
        if ((params.row.voice.current || params.row.voice.cached) > 1e6) {
          return 1e6
        } else if (
          (params.row.voice.cached || params.row.voice.current) <= 1e6 &&
          (params.row.voice.cached || params.row.voice.current) !== 0
        ) {
          return params.row.voice.remaining / (params.row.voice.cached || params.row.voice.current)
        } else {
          return -1
        }
      }
    },
    {
      field: 'sms',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>SMS Left</strong>
                </Typography>
                <Typography>
                  The remaining SMS of this current billing cycle.
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <TextsmsOutlinedIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: (params) => {
        if ((params.row.sms.cache || params.row.sms.current) > 1e6) {
          return <AllInclusiveIcon />
        } else if (
          (params.row.sms.cache || params.row.sms.current) <= 1e6 &&
          (params.row.sms.cache || params.row.sms.current) !== 0
        ) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {params.row.sms.remaining}
              &nbsp;/&nbsp;
              <strong>
                {Number(params.row.sms.cache || params.row.sms.current)}
              </strong>
            </Box>
          )
        } else {
          return <strong style={{ marginBottom: '12px' }}>-</strong>
        }
      },
      valueGetter: (params) => {
        if ((params.row.sms.cache || params.row.sms.current) > 1e6) {
          return 1e6
        } else if (
          (params.row.sms.cache || params.row.sms.current) <= 1e6 &&
          (params.row.sms.cache || params.row.sms.current) !== 0
        ) {
          return params.row.sms.remaining / (params.row.sms.cache || params.row.sms.current)
        } else {
          return -1
        }
      }
    },
    {
      field: 'status',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography>
                  <strong>Status</strong>
                </Typography>
                <Typography>
                  Current activation status of the user account.
                  <br />
                  {[
                    {
                      status: 'Active',
                      color: 'green'
                    },
                    {
                      status: 'Pending',
                      color: 'orange'
                    },
                    {
                      status: 'Terminated',
                      color: 'red'
                    },
                    {
                      status: 'Suspended',
                      color: 'gray'
                    }
                  ].map(({ status, color }) => {
                    return (
                      <>
                        <span
                          style={{
                            color,
                            fontWeight: 900,
                            fontSize: '20px'
                          }}
                        >
                          •
                        </span>{' '}
                        denotes{' '}
                        <span
                          style={{
                            color,
                            fontWeight: 900
                          }}
                        >
                          {' '}
                          &apos;{status}&apos;{' '}
                        </span>
                        <br />
                      </>
                    )
                  })}
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <SyncSharpIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            {formatStatusIcons(
              params.row.status || 'Pending'
            )}
          </>
        )
      },
      valueGetter: (params) => {
        return params.row.status || 'Pending'
      }
    },
    /* comment this for now as we dont show spending
    {
      field: 'spending',
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography variant='h5'>
                  <strong>Total Spend (SGD)</strong>
                </Typography>
                <Typography>
                  For total charges for this individual SIM for the current bill
                  cycle (current bill month). Includes monthly subscription,
                  PAYG, any other add-on charges (both 1 time or recurring).
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <PaidOutlinedIcon />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: () => {
        return '-'
      }
    },
    */
    {
      field: 'remarks',
      headerAlign: 'center',
      align: 'left',
      renderHeader: (params) => {
        return (
          <HtmlTooltip
            title={
              <Fragment key={params.colDef.field}>
                <Typography variant='h5'>
                  <strong>Remarks</strong>
                </Typography>
                <Typography>
                  Notes and remarks
                </Typography>
              </Fragment>
            }
          >
            <IconButton>
              <Description />
            </IconButton>
          </HtmlTooltip>
        )
      },
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.remarks}>
            <span>{params.row.remarks}</span>
          </Tooltip>
        )
      }
    },
    {
      field: 'action',
      headerName: '',
      flex: 1,
      renderCell: (params) => {
        const role: string = OrbitSession.get('role')
        return (
          FlagEditSim && (
            <>
              <IconButton
                id='editSimBtn'
                component={RouterLink}
                to='../edit-sim'
                state={{ teamMember: params.row.teamMember }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                id='deleteSimBtn'
                component={RouterLink}
                to={'#'}
                style={{ padding: 0, margin: 0 }}
                data-cy='actionBtn'
                onClick={() => handleDelete(params.row.msisdn)}
                sx={{ display: (role==='opsowner') ? 'block' : 'none' }}
              >
                <Delete style={{ color: 'darkblue' }} />
              </IconButton>
            </>
          )
        )
      }
    }
  ]

  const FlagEditSim = useOrbitFlag('dashboard-simList-editSimBtn')

  const formatStatusIcons = (input) => {
    let col

    switch (input.toLowerCase()) {
      case 'active':
        col = 'green'
        break
      case 'suspended':
        col = 'grey'
        break
      case 'terminated':
        col = 'red'
        break
      case 'pending':
        col = 'orange'
        break
      default:
        break
    }

    return (
      <HtmlTooltip title={`${input}`}>
        <CirclesIcon style={{ color: col, fontSize: 15 }} />
      </HtmlTooltip>
    )
  }

  const AccountToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>
            <strong>Assets</strong>
          </Typography>
          <Typography sx={{ color: '#818D9C', margin: '0 4px' }}>
            <i>
              {countSims('activeSims', teamMembers)} / {teamMembers.length}{' '}
              (Active)
            </i>
          </Typography>
        </Box>
        <CustomSearchbar />
        {customToolbarComponent}
      </GridToolbarContainer>
    )
  }

  const navigate = useNavigate()
  const handleConfirmDelete = async () => {
    setDialogOpen(false)
      const deleteSimResponse = await SimAPI.deleteSim(msisdn)
      if (deleteSimResponse && deleteSimResponse.data?.message) {
        toast.success('SIM successfully deleted.', {
          autoClose: 3000,
          hideProgressBar: false
        })
        setTimeout(() => {
          navigate(-1)
          refresh()
        }, 3000)
      } else {
        toast.error(
          'There was an issue deleting the SIM, please try again later.'
        )
      }
  }

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <Box>
        <div id='teamMember__dataGrid'>
          <MUIDataGrid
            rows={rows}
            columns={columns}
            components={{ Toolbar: AccountToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
          />
        </div>
      </Box>
      <DeleteDialog
        isDialogOpen={isDialogOpen}
        setDialogOpen={setDialogOpen}
        handleConfirmDelete={handleConfirmDelete}
        msisdn={msisdn}
      />
    </Suspense>
  )
}
